import React from "react";
import { ReactComponent as MinusBlack } from "../../assets/images/minus-black.svg";
import { ReactComponent as PlusBlack } from "../../assets/images/plus-black.svg";
import { currency as defaultCurrency } from "../../constants";

const PER_UNIT = "per unit";
const BULK = "bulk";

const Check = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1.5 6.3 4.4 9.2 10.5 3" />
  </svg>
);

/**
 * What the add-on IS, falling back to how it is priced.
 *
 * Recognising the product beats describing the pricing: the tile already says
 * "per ticket" in words, so a ticket glyph beside those words tells the buyer
 * nothing, while a shield tells them what they are buying at a glance.
 *
 * Matching a free-text name is a guess, but a guess that costs nothing here:
 * anything unrecognised drops to the type glyph, which is a real answer rather
 * than a blank tile. Add a pattern when a new kind of add-on starts selling.
 */
const PRODUCT_GLYPHS = [
  {
    name: "shield",
    match: /protect|insur|refund|guarantee|cover|assur/i,
    paths: (
      <React.Fragment>
        <path d="M12 3l7 3v5.5c0 4.3-2.9 7.7-7 9.5-4.1-1.8-7-5.2-7-9.5V6z" />
        <path d="M9 12l2.2 2.2L15.5 10" />
      </React.Fragment>
    ),
  },
  {
    name: "car",
    match: /park|valet|\bcar\b/i,
    paths: (
      <React.Fragment>
        <path d="M3 13l2-5.5A2 2 0 0 1 6.9 6h10.2a2 2 0 0 1 1.9 1.5L21 13" />
        <path d="M3 13h18v5H3z" />
        <circle cx="7" cy="15.5" r="1.1" />
        <circle cx="17" cy="15.5" r="1.1" />
      </React.Fragment>
    ),
  },
  {
    name: "bus",
    match: /shuttle|bus|transfer|transport|ride/i,
    paths: (
      <React.Fragment>
        <path d="M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5V17H4z" />
        <path d="M4 10h16" />
        <path d="M7 17v2M17 17v2" />
        <circle cx="7.5" cy="14" r="1" />
        <circle cx="16.5" cy="14" r="1" />
      </React.Fragment>
    ),
  },
  {
    name: "locker",
    match: /locker|bag|storage|cloak|luggage/i,
    paths: (
      <React.Fragment>
        <path d="M6 8.5h12l-1 11.5H7z" />
        <path d="M9 8.5V6a3 3 0 0 1 6 0v2.5" />
      </React.Fragment>
    ),
  },
  {
    name: "cup",
    match: /drink|bar\b|beverage|water|cocktail/i,
    paths: (
      <React.Fragment>
        <path d="M6 7h12l-1.2 12.2a2 2 0 0 1-2 1.8H9.2a2 2 0 0 1-2-1.8z" />
        <path d="M6.6 12h10.8" />
      </React.Fragment>
    ),
  },
  {
    name: "shirt",
    match: /shirt|merch|tee|hoodie|cap\b|wear/i,
    paths: (
      <path d="M9 4l3 1.6L15 4l5 3-2.2 3.4L16 9.4V20H8V9.4l-1.8 1L4 7z" />
    ),
  },
  {
    name: "bolt",
    match: /fast|skip|priorit|express|queue|line/i,
    paths: <path d="M13.2 2.5 4.5 14h6.6l-1.3 7.5L19.5 10h-6.6z" />,
  },
];

const TYPE_GLYPHS = {
  // One ticket, ticked: bought for particular seats.
  [PER_UNIT]: {
    name: "ticket-check",
    paths: (
      <React.Fragment>
        <path d="M3 8.5A1.5 1.5 0 0 0 4.5 7h15A1.5 1.5 0 0 0 21 8.5v2a2 2 0 0 0 0 3v2a1.5 1.5 0 0 0-1.5 1.5h-15A1.5 1.5 0 0 0 3 15.5v-2a2 2 0 0 0 0-3z" />
        <path d="M9 12l2 2 4-4" />
      </React.Fragment>
    ),
  },
  // Stacked tickets: it lands on every one of them.
  [BULK]: {
    name: "tickets",
    paths: (
      <React.Fragment>
        <path d="M6 6h13a1 1 0 0 1 1 1v2a1.8 1.8 0 0 0 0 3.6V15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-2.4A1.8 1.8 0 0 0 5 9V7a1 1 0 0 1 1-1z" />
        <path d="M3 10v8a1 1 0 0 0 1 1h13" />
      </React.Fragment>
    ),
  },
};

// A tag: one per order, whoever is coming.
const TAG_GLYPH = {
  name: "tag",
  paths: (
    <React.Fragment>
      <path d="M3.5 11.2V4.8a1.3 1.3 0 0 1 1.3-1.3h6.4a1.3 1.3 0 0 1 .9.4l8 8a1.3 1.3 0 0 1 0 1.9l-6.4 6.4a1.3 1.3 0 0 1-1.9 0l-8-8a1.3 1.3 0 0 1-.3-1z" />
      <circle cx="7.8" cy="7.8" r="1.3" />
    </React.Fragment>
  ),
};

const AddonGlyph = ({ name = "", type }) => {
  const glyph =
    PRODUCT_GLYPHS.find((candidate) => candidate.match.test(name)) ||
    TYPE_GLYPHS[type] ||
    TAG_GLYPH;

  return (
    <svg
      className="addon__glyph"
      data-glyph={glyph.name}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyph.paths}
    </svg>
  );
};

/**
 * The add-ons step.
 *
 * A per-unit add-on has NO quantity stepper: the buyer taps the tickets it
 * should cover and the quantity is however many they tapped. One control
 * instead of two that can disagree, so it is impossible to pay for three
 * protections and assign two.
 */
const Addons = ({
  addons = [],
  selectedAddons = {},
  ticketOptions = [],
  currency = defaultCurrency,
  handleAddonChange = () => {},
  handleTicketAssignment = () => {},
  handleAddonCountChange = () => {},
}) => {
  const ticketCount = ticketOptions.length;

  const typeLabel = (addon) => {
    if (addon.type === PER_UNIT) return "per ticket";
    if (addon.type === BULK) {
      return `applies to all ${ticketCount} ticket${ticketCount === 1 ? "" : "s"}`;
    }
    return "one per order";
  };

  const currencyOf = (addon) => addon.currency || currency;

  const selected = addons.filter((addon) => selectedAddons[addon.id]);
  const total = selected.reduce(
    (sum, addon) => sum + selectedAddons[addon.id].count * addon.price,
    0
  );
  // Only sum what shares a currency. A region-less add-on billed in the
  // selected currency beside a region-scoped one is not a number worth adding.
  const currencies = new Set(selected.map(currencyOf));
  const showTotal = selected.length > 0 && currencies.size === 1;

  return (
    <div className="addons-list">
      {addons.map((addon, index) => {
        const selection = selectedAddons[addon.id];
        const isSelected = Boolean(selection);
        const assigned = selection?.ticketIndices || [];
        // With one ticket there is no choice to make -- see the note below.
        const showPicker =
          isSelected && addon.type === PER_UNIT && ticketCount > 1;
        // The legacy cart flow has no individual tickets to assign to -- its
        // order items are one row per tier with a quantity -- so there the
        // buyer still needs a way to say "two of these".
        const showStepper =
          isSelected && addon.type === PER_UNIT && ticketCount === 0;
        const addonCurrency = currencyOf(addon);
        const subtotal = (selection?.count || 0) * addon.price;

        return (
          <div
            className={"addon" + (isSelected ? " addon--selected" : "")}
            key={addon.id ?? index}
          >
            <div
              className="addon__body"
              role="checkbox"
              tabIndex={0}
              aria-checked={isSelected}
              aria-label={`${addon.name}, ${addon.price} ${addonCurrency} ${typeLabel(
                addon
              )}`}
              onClick={() => handleAddonChange(addon)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleAddonChange(addon);
                }
              }}
            >
              <span className="addon__badge" aria-hidden="true">
                <Check className="addon__badge-icon" />
              </span>
              <AddonGlyph name={addon.name} type={addon.type} />
              <span className="addon__name">{addon.name}</span>
              <span className="addon__price">
                <span className="addon__price-value">
                  {addon.price} {addonCurrency}
                </span>
                <span className="addon__price-unit"> · {typeLabel(addon)}</span>
              </span>
              {/* Shown outright rather than behind a chevron nobody taps: this
                  is the copy that sells the add-on. */}
              {addon.description && (
                <span className="addon__description">{addon.description}</span>
              )}
            </div>

            {isSelected && addon.type === PER_UNIT && ticketCount === 1 && (
              <p className="addon__note">Covers your ticket.</p>
            )}

            {isSelected && addon.type === BULK && (
              <p className="addon__note">{`Applies to all ${ticketCount} ticket${
                ticketCount === 1 ? "" : "s"
              }.`}</p>
            )}

            {showPicker && (
              <div className="addon__picker">
                <div className="addon__picker-header">
                  <span className="addon__picker-title">On which tickets?</span>
                  <span className="addon__picker-count">
                    {assigned.length} of {ticketCount}
                  </span>
                </div>
                <div className="addon__chips">
                  {ticketOptions.map((ticket) => {
                    const checked = assigned.includes(ticket.index);
                    return (
                      <button
                        type="button"
                        key={ticket.index}
                        className={
                          "addon__chip" + (checked ? " addon__chip--on" : "")
                        }
                        aria-pressed={checked}
                        onClick={() => handleTicketAssignment(addon, ticket.index)}
                      >
                        <span className="addon__chip-mark" aria-hidden="true">
                          <Check className="addon__chip-icon" />
                        </span>
                        <span className="addon__chip-label">{ticket.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="addon__subtotal">
                  <span>Subtotal</span>
                  <span className="addon__subtotal-value">
                    {subtotal} {addonCurrency}
                  </span>
                </div>
              </div>
            )}

            {showStepper && (
              <div className="addon__stepper">
                <span className="addon__stepper-label">How many?</span>
                <div className="addon__stepper-controls">
                  <button
                    type="button"
                    className="addon__stepper-button"
                    aria-label={`Remove one ${addon.name}`}
                    onClick={() => handleAddonCountChange(addon, "subtract")}
                  >
                    <MinusBlack className="addon__stepper-icon" />
                  </button>
                  <span className="addon__stepper-count">
                    {selection?.count || 0}
                  </span>
                  <button
                    type="button"
                    className="addon__stepper-button"
                    aria-label={`Add one ${addon.name}`}
                    onClick={() => handleAddonCountChange(addon, "add")}
                  >
                    <PlusBlack className="addon__stepper-icon" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* The step used to end without ever stating a price. */}
      {showTotal && (
        <div className="addons-list__total">
          <span>
            {selected.length} add-on{selected.length === 1 ? "" : "s"}
          </span>
          <span className="addons-list__total-value">
            {total} {currencyOf(selected[0])}
          </span>
        </div>
      )}
    </div>
  );
};

export default Addons;
